import { CancelFunctions } from 'components/common/types';
import { HEADERS_CONTENT_TYPE, HTTP_METHODS, URLS } from 'modules/api/constants';
import {
  AvailabilityReportData,
  TransformedAvailabilityExportRequest,
  TransformedAvailabilityRequest,
} from 'components/pages/AvailabilityReport/AvailabilityReport.types';
import handleRequest from 'modules/api/common';
import createRequest from 'modules/api/request';
import {
  ADVANCED_AVAILABILITY_POLL_CANCEL_KEY,
  ADVANCED_AVAILABILITY_POLL_TIMEOUT_MS,
  ADVANCED_AVAILABILITY_STATUS_REQUEST_CANCEL_KEY,
  AdvancedAvailabilityPayload,
  AdvancedAvailabilityResponse,
  AdvancedAvailabilityStatusData,
  ONGOING_ADVANCED_AVAILABILITY_STATUSES,
} from 'consts/advancedAvailabilityReportStatus';
import { pollingWithBackoff } from 'modules/utils/pollingWithBackoff';

export const getDirectSalesAvailabilityReport = async (
  marketId: string,
  data: TransformedAvailabilityRequest,
  cancelFunctions: CancelFunctions,
): Promise<AvailabilityReportData> => {
  const reqOptions = {
    url: URLS().FETCH_DIRECT_SALES_AVAILABILITY_REPORT(marketId),
    method: HTTP_METHODS.POST,
    data,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 0,
  };

  return handleRequest(reqOptions, cancelFunctions, 'FETCH_DIRECT_SALES_AVAILABILITY_REPORT');
};

export const exportDirectSalesAvailabilityReport = async (
  marketId: string,
  data: TransformedAvailabilityExportRequest,
  cancelFunctions: CancelFunctions,
): Promise<{
  data: Blob;
  headers: Record<string, string>;
}> => {
  const { send, cancel } = createRequest({
    url: URLS().DOWNLOAD_DIRECT_SALES_AVAILABILITY_REPORT(marketId),
    method: HTTP_METHODS.POST,
    data,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
      'Accept-Encoding': 'gzip, deflate',
    },
    responseType: 'blob' as const,
    ignoreNotifyError: true,
    decompress: true,
  });

  if (cancelFunctions.EXPORT_DIRECT_SALES_AVAILABILITY_REPORT) {
    cancelFunctions.EXPORT_DIRECT_SALES_AVAILABILITY_REPORT();
  }
  cancelFunctions.EXPORT_DIRECT_SALES_AVAILABILITY_REPORT = cancel;

  return send();
};

export const fetchAdvancedDirectSalesAvailabilityReportExportId = async (
  marketId: string,
  data: { campaignId: string; dealLineIds: string[] },
  cancelFunctions: CancelFunctions,
): Promise<{ exportId: string }> => {
  const reqOptions = {
    url: URLS().FETCH_ADVANCED_DIRECT_SALES_AVAILABILITY_REPORT_EXPORT_ID(marketId),
    method: HTTP_METHODS.POST,
    data,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    ignoreNotifyError: true,
    showLoader: false,
  };

  return handleRequest(reqOptions, cancelFunctions, 'FETCH_ADVANCED_DIRECT_SALES_AVAILABILITY_REPORT_EXPORT_ID');
};

export const fetchAdvancedDirectSalesAvailabilityReport = {
  cancel: () => {},
  poll: async ({
    marketId,
    exportId,
    cancelFunctions = {},
  }: AdvancedAvailabilityPayload): Promise<AdvancedAvailabilityResponse> => {
    const reqOptions = {
      url: URLS().FETCH_ADVANCED_DIRECT_SALES_AVAILABILITY(marketId, exportId),
      method: HTTP_METHODS.GET,
      ignoreNotifyError: true,
      showLoader: false,
    };

    const fetchStatus = async (): Promise<AdvancedAvailabilityStatusData> => {
      const { send, cancel } = createRequest(reqOptions);

      cancelFunctions[ADVANCED_AVAILABILITY_STATUS_REQUEST_CANCEL_KEY]?.();
      cancelFunctions[ADVANCED_AVAILABILITY_STATUS_REQUEST_CANCEL_KEY] = cancel;

      const response = await send();

      return response.data;
    };

    fetchAdvancedDirectSalesAvailabilityReport.cancel = () => {
      cancelFunctions[ADVANCED_AVAILABILITY_POLL_CANCEL_KEY]?.();
    };

    try {
      const data = await pollingWithBackoff({
        poll: fetchStatus,
        shouldStop: (result) => !ONGOING_ADVANCED_AVAILABILITY_STATUSES.includes(result.status),
        timeout: ADVANCED_AVAILABILITY_POLL_TIMEOUT_MS,
        cancelFunctions,
        cancelKey: ADVANCED_AVAILABILITY_POLL_CANCEL_KEY,
        requestCancelKeys: [ADVANCED_AVAILABILITY_STATUS_REQUEST_CANCEL_KEY],
      });

      return { data };
    } finally {
      fetchAdvancedDirectSalesAvailabilityReport.cancel = () => {};
    }
  },
};
